// src/services/commune.service.ts

import api from './api';
import { API_CONFIG } from '../config/api.config';
import type { ApiResponse, Commune, AvecGroup, Village, PaginationInfo } from '../types/api.types';

interface CommuneFilters {
  search?: string;
  provinceId?: string;
  regionId?: string;
  country?: string;
  isActive?: boolean;
  sortBy?: 'communeName' | 'provinceId' | 'totalVillages' | 'totalGroups' | 'createdAt';
  sortOrder?: 'asc' | 'desc';
  page?: number;
  limit?: number;
}

interface CommuneStats {
  totalVillages: number;
  totalGroups: number;
  totalMembers: number;
  totalSavings: number;
  totalCredits: number;
  activeVillages: number;
  activeGroups: number;
  formingGroups: number;
  suspendedGroups: number;
}

interface CommuneDashboard {
  commune: Commune;
  stats: CommuneStats;
  recentVillages: Village[];
  recentGroups: AvecGroup[];
  financialSummary: {
    totalFunds: number;
    totalSavings: number;
    totalCredits: number;
    totalSolidarityFund: number;
  };
}

interface CreateCommuneData {
  communeName: string;
  provinceId: string;
  communalAdminId?: string;
}

interface UpdateCommuneData extends Partial<CreateCommuneData> {
  isActive?: boolean;
}


// Income Activities interfaces
interface GroupActivity {
  id: string;
  groupId: string;
  activityType: 'crop_sale' | 'livestock_sale' | 'craft_sale' | 'food_processing' | 'trade_business' | 'service_provision' | 'rental_income' | 'other';
  activityName: string;
  activityDescription: string;
  incomeAmount: string;
  expenseAmount: string;
  netIncome: string;
  activityDate: string;
  organizedBy: string;
  participants: any[];
  profitSharingMethod: string;
  profitDistribution: any;
  groupFundContribution: string;
  groupFundPercentage: string;
  status: 'planned' | 'in_progress' | 'completed' | 'cancelled';
  completedDate: string | null;
  location: string;
  season: string | null;
  recurringType: 'one_time' | 'weekly' | 'monthly' | 'seasonal' | 'annual';
  Group: {
    id: string;
    groupName: string;
    groupCode: string;
  };
  Organizer: {
    id: string;
    memberNumber: string;
    User: {
      firstName: string;
      lastName: string;
      email: string;
    };
  };
  createdAt: string;
  updatedAt: string;
}

interface OtherIncome {
  id: string;
  groupId: string;
  amount: string;
  source: 'donation' | 'sale' | 'grant' | 'gift' | 'refund' | 'interest' | 'other';
  description: string;
  date: string;
  receivedBy: string;
  notes: string;
  Group: {
    id: string;
    groupName: string;
    groupCode: string;
  };
  ReceivedByUser: {
    firstName: string;
    lastName: string;
    email: string;
  };
  createdAt: string;
  updatedAt: string;
}

interface GroupActivitiesResponse {
  communeId: string;
  communeName: string;
  groupActivities: GroupActivity[];
  pagination: PaginationInfo;
  summary: {
    totalActivities: number;
    plannedActivities: number;
    inProgressActivities: number;
    completedActivities: number;
    cancelledActivities: number;
    totalIncomeAmount: number;
    totalExpenseAmount: number;
    totalNetIncome: number;
    totalGroupFundContribution: number;
  };
  groupBreakdown: {
    groupId: string;
    groupName: string;
    totalActivities: number;
    plannedActivities: number;
    inProgressActivities: number;
    completedActivities: number;
    cancelledActivities: number;
    totalIncomeAmount: number;
    totalNetIncome: number;
    totalGroupFundContribution: number;
  }[];
  filters: {
    sortBy: string;
    sortOrder: string;
    activityType?: string;
    status?: string;
    season?: string;
    recurringType?: string;
    startDate?: string;
    endDate?: string;
  };
}

interface OtherIncomeResponse {
  communeId: string;
  communeName: string;
  otherIncome: OtherIncome[];
  pagination: PaginationInfo;
  summary: {
    totalIncomeRecords: number;
    totalAmount: number;
    sourceBreakdown: Record<string, {
      count: number;
      totalAmount: number;
    }>;
  };
  groupBreakdown: {
    groupId: string;
    groupName: string;
    totalIncomeRecords: number;
    totalAmount: number;
  }[];
  filters: {
    sortBy: string;
    sortOrder: string;
    source?: string;
    startDate?: string;
    endDate?: string;
  };
}

interface IncomeSummaryResponse {
  communeId: string;
  communeName: string;
  totalIncomeAmount: number;
  activitiesIncome: number;
  otherIncome: number;
  groupFundContributions: number;
  summary: {
    totalGroups: number;
    activeGroups: number;
    totalActivities: number;
    completedActivities: number;
    otherIncomeRecords: number;
  };
  groupComparison: {
    groupId: string;
    groupName: string;
    totalIncome: number;
    activitiesIncome: number;
    otherIncome: number;
    groupFundContribution: number;
    activitiesCount: number;
    otherIncomeRecords: number;
  }[];
  filters: {
    period: string;
    startDate?: string;
    endDate?: string;
  };
}

interface GroupActivitiesFilters {
  activityType?: string;
  status?: string;
  season?: string;
  recurringType?: string;
  startDate?: string;
  endDate?: string;
  organizedBy?: string;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: string;
}

interface OtherIncomeFilters {
  source?: string;
  startDate?: string;
  endDate?: string;
  receivedBy?: string;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: string;
}

interface IncomeSummaryFilters {
  startDate?: string;
  endDate?: string;
  period?: 'week' | 'month' | 'quarter' | 'year';
}

// Weekly Contributions interfaces
interface CommuneWeeklyContribution {
  id: string;
  groupId: string;
  groupName: string;
  groupCode: string;
  memberId: string;
  memberName: string;
  memberNumber: string;
  contributionWeek: string;
  weekNumber: number;
  cycleNumber: number;
  expectedAmount: number;
  actualAmount: number | null;
  lateFeeAmount: number | null;
  paymentMethod: 'cash' | 'mobile_money' | 'bank_transfer' | 'agent_collection' | null;
  paymentReference?: string;
  status: 'pending' | 'collected' | 'late' | 'missed' | 'partial' | 'waived';
  isLate: boolean;
  dueDate: string;
  collectedAt: string | null;
  collectedBy: string | null;
  collectedByName: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

interface WeeklyContributionsFilters {
  startDate?: string;
  endDate?: string;
  groupId?: string;
  memberId?: string;
  status?: 'pending' | 'collected' | 'late' | 'missed' | 'partial' | 'waived';
  paymentMethod?: 'cash' | 'mobile_money' | 'bank_transfer' | 'agent_collection';
  weekNumber?: number;
  cycleNumber?: number;
  isLate?: boolean;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: string;
}

interface WeeklyContributionsResponse {
  communeId: string;
  communeName: string;
  weeklyContributions: CommuneWeeklyContribution[];
  pagination: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    itemsPerPage: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
  };
  summary: {
    totalContributionRecords: number;
    totalExpectedAmount: number;
    totalCollectedAmount: number;
    totalLateFees: number;
    collectionRate: number;
    groupsWithContributions: number;
    averageContributionPerGroup: number;
  };
  statusBreakdown: {
    pending: number;
    collected: number;
    late: number;
    missed: number;
    partial: number;
    waived: number;
  };
  groupBreakdown: {
    groupId: string;
    groupName: string;
    groupCode: string;
    contributionCount: number;
    totalExpectedAmount: number;
    totalCollectedAmount: number;
    totalLateFees: number;
    averageAmount: number;
    collectionRate: number;
  }[];
  filters: {
    sortBy: string;
    sortOrder: string;
    startDate?: string;
    endDate?: string;
    groupId?: string;
    status?: string;
    paymentMethod?: string;
    weekNumber?: number;
    cycleNumber?: number;
    isLate?: boolean;
  };
}

// Group Expenses interfaces
interface CommuneGroupExpense {
  id: string;
  groupId: string;
  groupName: string;
  groupCode: string;
  description: string;
  amount: number;
  expenseDate: string;
  recordedBy: string;
  recordedByName?: string;
  transactionId?: string;
  createdAt: string;
  updatedAt: string;
}

interface GroupExpensesFilters {
  startDate?: string;
  endDate?: string;
  groupId?: string;
  description?: string;
  recordedBy?: string;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: string;
}

interface GroupExpensesResponse {
  communeId: string;
  communeName: string;
  groupExpenses: CommuneGroupExpense[];
  pagination: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    itemsPerPage: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
  };
  summary: {
    totalExpenseRecords: number;
    totalAmount: number;
    groupsWithExpenses: number;
    averageExpensePerGroup: number;
  };
  groupBreakdown: {
    groupId: string;
    groupName: string;
    groupCode: string;
    expenseCount: number;
    totalAmount: number;
    averageAmount: number;
  }[];
  filters: {
    sortBy: string;
    sortOrder: string;
    startDate?: string;
    endDate?: string;
    groupId?: string;
    description?: string;
  };
}

class CommuneService {
  /**
   * Get list of communes with filtering and pagination
   */
  async getCommunes(filters: CommuneFilters = {}): Promise<ApiResponse<{
    communes: Commune[];
    pagination: PaginationInfo;
  }>> {
    const params = new URLSearchParams();
    
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        params.append(key, String(value));
      }
    });

    const response = await api.get(`${API_CONFIG.COMMUNES.BASE}?${params.toString()}`);

    // Normalize various possible API response shapes into a consistent structure
    let communes: Commune[] = [];
    let pagination: PaginationInfo = {
      page: 1,
      limit: 10,
      totalItems: 0,
      totalPages: 1,
      hasNext: false,
      hasPrev: false,
    };

    if (Array.isArray(response.data)) {
      // Direct array response
      communes = response.data as Commune[];
      pagination.totalItems = communes.length;
    } else if (response.data?.data?.communes && Array.isArray(response.data.data.communes)) {
      // Nested response data.data.communes
      communes = response.data.data.communes as Commune[];
      pagination = (response.data.data.pagination || pagination) as PaginationInfo;
    } else if (response.data?.data && Array.isArray(response.data.data)) {
      // Nested array at data.data
      communes = response.data.data as Commune[];
      pagination = (response.data.pagination || pagination) as PaginationInfo;
    } else if (response.data?.communes && Array.isArray(response.data.communes)) {
      // Wrapped in data.communes
      communes = response.data.communes as Commune[];
      pagination = (response.data.pagination || pagination) as PaginationInfo;
    } else if (response.data) {
      // Fallback - assume data contains communes array
      communes = response.data as Commune[];
    }

    return {
      // Extra fields present on ApiResponse in our codebase
      group: null as any,
      groupName: null as any,
      dashboard: null as any,
      success: (response as any).success ?? true,
      message: (response as any).message ?? 'Success',
      data: {
        communes,
        pagination,
      },
      timestamp: (response as any).timestamp ?? new Date().toISOString(),
    } as ApiResponse<{ communes: Commune[]; pagination: PaginationInfo }>;
  }

  /**
   * Get commune by ID
   */
  async getCommuneById(communeId: string): Promise<ApiResponse<Commune>> {
    const axiosResponse = await api.get(API_CONFIG.COMMUNES.BY_ID(communeId));
    const raw = (axiosResponse as any).data;

    // Extract the commune object from various possible response shapes
    // Expected from backend sample:
    // { success, message, data: { message, data: { commune: { ... } } }, timestamp }
    const commune: Commune | undefined =
      raw?.data?.data?.commune ||
      raw?.data?.commune ||
      raw?.commune ||
      undefined;

    return {
      group: null as any,
      groupName: null as any,
      dashboard: null as any,
      success: raw?.success ?? true,
      message: raw?.message ?? raw?.data?.message ?? 'Success',
      data: commune as Commune,
      timestamp: raw?.timestamp ?? new Date().toISOString(),
    } as ApiResponse<Commune>;
  }

  /**
   * Create new commune
   */
  async createCommune(communeData: CreateCommuneData): Promise<ApiResponse<Commune>> {
    const response = await api.post(API_CONFIG.COMMUNES.BASE, communeData);
    return response;
  }

  /**
   * Update commune
   */
  async updateCommune(communeId: string, updateData: UpdateCommuneData): Promise<ApiResponse<Commune>> {
    const response = await api.put(API_CONFIG.COMMUNES.BY_ID(communeId), updateData);
    return response;
  }

  /**
   * Delete commune
   */
  async deleteCommune(communeId: string, reason?: string): Promise<ApiResponse<void>> {
    const response = await api.delete(API_CONFIG.COMMUNES.BY_ID(communeId), {
      data: reason ? { reason } : undefined
    });
    return response;
  }

  /**
   * Get groups in a commune
   */
  async getCommuneGroups(communeId: string, filters: {
    status?: string;
    page?: number;
    limit?: number;
  } = {}): Promise<ApiResponse<{
    groups: AvecGroup[];
    pagination: PaginationInfo;
  }>> {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        params.append(key, String(value));
      }
    });

    const response = await api.get(`${API_CONFIG.COMMUNES.GROUPS(communeId)}?${params.toString()}`);
    return response;
  }

  /**
   * Get villages in a commune
   */
  async getCommuneVillages(communeId: string, filters: {
    isActive?: boolean;
    page?: number;
    limit?: number;
  } = {}): Promise<ApiResponse<{
    villages: Village[];
    pagination: PaginationInfo;
  }>> {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        params.append(key, String(value));
      }
    });

    const response = await api.get(`${API_CONFIG.COMMUNES.VILLAGES(communeId)}?${params.toString()}`);
    return response;
  }

  /**
   * Get commune statistics
   */
  async getCommuneStatistics(communeId: string): Promise<ApiResponse<CommuneStats>> {
    const response = await api.get(API_CONFIG.COMMUNES.STATISTICS(communeId));
    return response;
  }

  /**
   * Get commune dashboard data
   */
  async getCommuneDashboard(communeId: string): Promise<ApiResponse<CommuneDashboard>> {
    const response = await api.get(API_CONFIG.COMMUNES.DASHBOARD(communeId));
    return response;
  }

  /**
   * Get commune analytics dashboard data
   */
  async getCommuneAnalyticsDashboard(communeId: string): Promise<ApiResponse<any>> {
    const response = await api.get(`/analytics/communes/${communeId}/dashboard`);
    return response;
  }

  /**
   * Assign communal admin to commune
   */
  async assignCommunalAdmin(communeId: string, adminId: string): Promise<ApiResponse<{ commune: Commune }>> {
    const response = await api.post(API_CONFIG.COMMUNES.ASSIGN_ADMIN(communeId), {
      adminUserId: adminId
    });
    return {
      group: null as any,
      groupName: null as any,
      dashboard: null as any,
      success: response.success,
      message: response.message,
      data: { commune: response.data },
      timestamp: response.timestamp
    };
  }

  /**
   * Remove communal admin from commune
   */
  async removeCommunalAdmin(communeId: string): Promise<ApiResponse<Commune>> {
    const response = await api.put(API_CONFIG.COMMUNES.BY_ID(communeId), {
      communalAdminId: null
    });
    return response;
  }

  /**
   * Unassign communal admin from commune (alias for removeCommunalAdmin)
   */
  async unassignCommunalAdmin(communeId: string): Promise<ApiResponse<{ commune: Commune }>> {
    const response = await this.removeCommunalAdmin(communeId);
    return {
      group: (response as any).group ?? null,
      groupName: (response as any).groupName ?? null,
      dashboard: (response as any).dashboard ?? null,
      success: response.success,
      message: response.message,
      data: { commune: response.data },
      timestamp: response.timestamp,
    };
  }

  /**
   * Get available communal admins (not assigned to any commune)
   */
  async getAvailableCommunalAdmins(): Promise<ApiResponse<any[]>> {
    const response = await api.get('/staff?role=communal_admin&available=true');
    return response;
  }

  // Income Activities Methods

  /**
   * Get commune-wide group activities with filtering and statistics
   */
  async getCommuneGroupActivities(
    communeId: string, 
    filters?: GroupActivitiesFilters
  ): Promise<ApiResponse<GroupActivitiesResponse>> {
    const params = new URLSearchParams();
    
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.append(key, value.toString());
        }
      });
    }

    const queryString = params.toString();
    const url = `/communes/${communeId}/group-activities${queryString ? `?${queryString}` : ''}`;
    
    const response = await api.get(url);
    return response;
  }

  /**
   * Get commune-wide other income with filtering and statistics  
   */
  async getCommuneOtherIncome(
    communeId: string,
    filters?: OtherIncomeFilters
  ): Promise<ApiResponse<OtherIncomeResponse>> {
    const params = new URLSearchParams();
    
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.append(key, value.toString());
        }
      });
    }

    const queryString = params.toString();
    const url = `/communes/${communeId}/other-income${queryString ? `?${queryString}` : ''}`;
    
    const response = await api.get(url);
    return response;
  }

  /**
   * Get commune-wide income activities summary (combined activities and other income)
   */
  async getCommuneIncomeSummary(
    communeId: string,
    filters?: IncomeSummaryFilters
  ): Promise<ApiResponse<IncomeSummaryResponse>> {
    const params = new URLSearchParams();
    
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.append(key, value.toString());
        }
      });
    }

    const queryString = params.toString();
    const url = `/communes/${communeId}/income-summary${queryString ? `?${queryString}` : ''}`;
    
    const response = await api.get(url);
    return response;
  }

  /**
   * Get commune-wide weekly contributions with filtering and statistics  
   */
  async getCommuneWeeklyContributions(
    communeId: string,
    filters?: WeeklyContributionsFilters
  ): Promise<ApiResponse<WeeklyContributionsResponse>> {
    const params = new URLSearchParams();
    
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.append(key, value.toString());
        }
      });
    }

    const queryString = params.toString();
    const url = `/communes/${communeId}/weekly-contributions${queryString ? `?${queryString}` : ''}`;
    
    const response = await api.get(url);
    return response;
  }

  /**
   * Get commune-wide group expenses with filtering and statistics  
   */
  async getCommuneGroupExpenses(
    communeId: string,
    filters?: GroupExpensesFilters
  ): Promise<ApiResponse<GroupExpensesResponse>> {
    const params = new URLSearchParams();
    
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.append(key, value.toString());
        }
      });
    }

    const queryString = params.toString();
    const url = `/communes/${communeId}/group-expenses${queryString ? `?${queryString}` : ''}`;
    
    const response = await api.get(url);
    return response;
  }

  /**
   * Get commune-wide solidarity contributions with filtering and statistics  
   */
  async getCommuneSolidarityContributions(
    communeId: string,
    filters?: {
      startDate?: string;
      endDate?: string;
      contributionWeek?: string;
      groupId?: string;
      memberId?: string;
      status?: 'pending' | 'collected' | 'late' | 'missed' | 'partial' | 'waived';
      paymentMethod?: 'cash' | 'mobile_money' | 'bank_transfer' | 'agent_collection';
      page?: number;
      limit?: number;
      sortBy?: string;
      sortOrder?: string;
    }
  ): Promise<ApiResponse<any>> {
    const params = new URLSearchParams();
    
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.append(key, value.toString());
        }
      });
    }

    const queryString = params.toString();
    const url = `/communes/${communeId}/solidarity-contributions${queryString ? `?${queryString}` : ''}`;
    
    const response = await api.get(url);
    return response;
  }
}

export default new CommuneService();